import React, { useState, useRef, useCallback } from 'react';
import { HiOutlineUpload, HiOutlineDocumentText } from 'react-icons/hi';
import axiosInstance from '../../../api/axiosConfig';
import BatchReview from './BatchReview';

const MAX_SIZE_MB = 50;

const UploadTab = ({ canCreate, canSend, gatewayReady }) => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [batch, setBatch] = useState(null);
    const [error, setError] = useState(null);
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef(null);

    const pickFile = (selected) => {
        setError(null);
        if (!selected) return;
        if (selected.type !== 'application/pdf' && !selected.name.toLowerCase().endsWith('.pdf')) {
            setError('Please select a PDF file.');
            return;
        }
        if (selected.size > MAX_SIZE_MB * 1024 * 1024) {
            setError(`File must be under ${MAX_SIZE_MB} MB.`);
            return;
        }
        setFile(selected);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        pickFile(e.dataTransfer.files?.[0]);
    };

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        setProgress(0);
        setError(null);
        setBatch(null);

        const form = new FormData();
        form.append('file', file);

        try {
            const res = await axiosInstance.post('/api/payslips/upload', form, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (e) => {
                    if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
                }
            });
            setBatch(res.data?.data || null);
            setFile(null);
            if (inputRef.current) inputRef.current.value = '';
        } catch (err) {
            setError(err.response?.data?.message || 'Upload failed.');
        } finally {
            setUploading(false);
            setProgress(0);
        }
    };

    const refreshBatch = useCallback(async () => {
        if (!batch?._id) return;
        try {
            const res = await axiosInstance.get(`/api/payslips/batches/${batch._id}`);
            setBatch(res.data?.data || batch);
        } catch {
            /* keep the current view on a transient error */
        }
    }, [batch]);

    return (
        <div className="pd-stack">
            <div className="pd-card">
                <div className="pd-card-head">
                    <div>
                        <h3>Upload payroll PDF</h3>
                        <p className="pd-muted">
                            The file is split into one PDF per employee using the “Employee Code” on each page.
                            Nothing is sent until you review the matches and confirm.
                        </p>
                    </div>
                </div>

                <div
                    className={`pd-drop ${dragging ? 'dragging' : ''} ${!canCreate ? 'disabled' : ''}`}
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={canCreate ? handleDrop : (e) => e.preventDefault()}
                    onClick={() => canCreate && inputRef.current?.click()}
                >
                    <HiOutlineDocumentText size={36} />
                    {file ? (
                        <>
                            <strong>{file.name}</strong>
                            <span className="pd-muted">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                        </>
                    ) : (
                        <>
                            <strong>{canCreate ? 'Drop the payroll PDF here, or click to browse' : 'You do not have permission to upload'}</strong>
                            <span className="pd-muted">PDF only, up to {MAX_SIZE_MB} MB</span>
                        </>
                    )}
                    <input
                        ref={inputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        hidden
                        onChange={(e) => pickFile(e.target.files?.[0])}
                    />
                </div>

                {error && <div className="pd-banner pd-banner-err">{error}</div>}

                {uploading && (
                    <div className="pd-progress">
                        <div className="pd-progress-bar" style={{ width: `${progress}%` }} />
                    </div>
                )}

                <div className="pd-actions">
                    <button
                        className="pd-btn pd-btn-primary"
                        onClick={handleUpload}
                        disabled={!file || uploading || !canCreate}
                    >
                        <HiOutlineUpload size={16} />
                        {uploading ? `Processing… ${progress}%` : 'Split & review'}
                    </button>
                    {file && !uploading && (
                        <button className="pd-btn pd-btn-ghost" onClick={() => { setFile(null); if (inputRef.current) inputRef.current.value = ''; }}>
                            Clear
                        </button>
                    )}
                </div>
            </div>

            {batch && (
                <BatchReview
                    batch={batch}
                    onRefresh={refreshBatch}
                    canSend={canSend}
                    gatewayReady={gatewayReady}
                />
            )}
        </div>
    );
};

export default UploadTab;
